import { Text } from "@smoothincorg/smooth-ui"
import React, { useEffect } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { BorderedButton } from "src/components/Button"
import { InputField } from "src/components/InputField"
import { formatDate } from "src/components/List/List"
import { SubmitButton } from "src/features/lifftter/components/SubmitButton"
import { CurrentAddressPostCode } from "src/features/lifftter/hooks/useLatestCurrentAddressPostCodeSWR"
import { CurrentAddress as CurrentAddressType } from "src/features/lifftter/hooks/useLatestCurrentAddressSWR"
import { useAddressSWR } from "src/hooks/useAddressSWR"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { camelCase } from "src/lib/aikagi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import { getHankakuFormUtils, getZenkakuFormUtils } from "src/lib/utils/form"
import useSWR, { mutate } from "swr"
import { useEditingId } from "../../hooks/useEditingId"
import { ModalEditableForm } from "../ModalEditableForm/ModalEditableForm"
import { sortById } from "../User/User"
import * as styles from "./CurrentAddress.css"
import { useNotification } from "@smoothincorg/smooth-ui"

const currentAddressFormDefaultValues = {
  prefecture: "",
  line1: "",
  line2: "",
  line3: "",
  buildingName: "",
  roomNumber: "",
  roomNumberKana: "",
  prefectureKana: "",
  line1Kana: "",
  line2Kana: "",
  line3Kana: "",
  buildingNameKana: "",
}

const CurrentAddress: React.FC<{
  currentAddressPostCode: CurrentAddressPostCode
  enableAutocomplete?: boolean
  proposalId: number
  resourceName?: string
}> = (props) => {
  const { currentAddressPostCode, enableAutocomplete = true, proposalId } = props
  const { getWithToken, patchWithToken, postWithToken } = useSmoothApi()
  const resourceName = props.resourceName
  const { editingId } = useEditingId(resourceName)
  const { showAlert, showSnackbar } = useNotification()

  const currentAddressEndPoint =
    `${SMOOTH_SMOOTHER_API_URL}/current_address_post_codes/${currentAddressPostCode.id}/current_address` as const

  const currentAddressSWR = useSWR(currentAddressEndPoint, async (url) => {
    if (currentAddressPostCode.id === 0) return null
    const response = await getWithToken(url)
    if (!response.data["data"]) return null
    return camelCase(response.data["data"]) as CurrentAddressType
  })

  const currentAddress = currentAddressSWR.data

  const currentPostCodeForm = useForm<{
    zipCode: string
  }>({
    defaultValues: {
      zipCode: "",
    },
  })

  const currentAddressForm = useForm<CurrentAddressType>({
    defaultValues: currentAddressFormDefaultValues,
    mode: "onChange",
  })

  const zipCodeValue = currentPostCodeForm.watch("zipCode")

  const addressSWR = useAddressSWR(zipCodeValue)

  const onSubmit: SubmitHandler<CurrentAddressType> = async (data) => {
    // SnakeCase関数使うとline1_kanaとかになるので手動で変換
    try {
      const zipCode = zipCodeValue
      const shouldCreatePostCode = editingId === 0
      const shouldCreateCurrentAddress = currentAddressSWR.data === null

      const currentAddressPostCodePayload = {
        current_address_post_code: {
          head: zipCode.slice(0, 3),
          tail: zipCode.slice(3, 7),
        },
      }

      const sendCurrentAddress = async (createdCurrentAddressPostCodeId: number) => {
        const payload = {
          current_address: {
            current_address_post_code_id: createdCurrentAddressPostCodeId,
            prefecture: data.prefecture,
            line_1: data.line1,
            line_2: data.line2,
            line_3: data.line3,
            building_name: data.buildingName,
            room_number: data.roomNumber,
            room_number_kana: data.roomNumberKana,
            prefecture_kana: data.prefectureKana,
            line_1_kana: data.line1Kana,
            line_2_kana: data.line2Kana,
            line_3_kana: data.line3Kana,
            building_name_kana: data.buildingNameKana,
          },
        }
        if (shouldCreateCurrentAddress) {
          await postWithToken(
            `${SMOOTH_SMOOTHER_API_URL}/current_address_post_codes/${createdCurrentAddressPostCodeId}/current_address`,
            payload
          )

          return
        }

        await patchWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/current_address_post_codes/${createdCurrentAddressPostCodeId}/current_address`,
          payload
        )
      }

      if (shouldCreatePostCode) {
        const res = await postWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/current_address_post_codes`,
          currentAddressPostCodePayload
        )

        const createdCurrentAddressPostCodeId = res.data["data"]["id"]

        await sendCurrentAddress(createdCurrentAddressPostCodeId)
      }

      if (!shouldCreatePostCode) {
        const res = await patchWithToken(
          `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/current_address_post_codes/${editingId}`,
          currentAddressPostCodePayload
        )

        const createdCurrentAddressPostCodeId = res.data["data"]["id"]

        await sendCurrentAddress(createdCurrentAddressPostCodeId)
      }
    } catch (e) {
      const error = e as Error
      showAlert("エラーが発生しました！: " + error.message)
      mutate(`${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/current_address_post_codes`)
      return
    }

    showSnackbar("住所を更新しました！", "success")

    mutate(`${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/current_address_post_codes`)
    mutate(currentAddressEndPoint)
  }

  useEffect(() => {
    if (currentAddressSWR.data) {
      currentAddressForm.reset({
        ...currentAddressSWR.data,
        roomNumberKana: currentAddressSWR.data.roomNumberKana || "",
      })
    }

    if (!editingId) {
      currentAddressForm.reset({
        prefecture: "",
        line1: "",
        line2: "",
        line3: "",
        buildingName: "",
        roomNumber: "",
        roomNumberKana: "",
        prefectureKana: "",
        line1Kana: "",
        line2Kana: "",
        line3Kana: "",
        buildingNameKana: "",
      })
    }
  }, [currentAddressForm, currentAddressSWR.data, editingId])

  useEffect(() => {
    if (currentAddressPostCode) {
      currentPostCodeForm.reset({
        zipCode: currentAddressPostCode.head + currentAddressPostCode.tail,
      })
    }

    if (!editingId) {
      currentPostCodeForm.reset({
        zipCode: "",
      })
    }
  }, [currentAddressPostCode, currentPostCodeForm, editingId])

  useEffect(() => {
    const result = addressSWR?.results?.[0]

    if (result && enableAutocomplete) {
      currentAddressForm.reset((c) => ({
        ...c,
        prefecture: c.prefecture || result.address1,
        line1: c.line1 || result.address2,
        line2: c.line2 || result.address3,
        prefectureKana: c.prefectureKana || result.kana1,
        line1Kana: c.line1Kana || result.kana2,
        line2Kana: c.line2Kana || result.kana3,
      }))
    }
  }, [addressSWR?.results, currentAddressForm, enableAutocomplete])

  const { registerAutoFillAsZenkaku } = getZenkakuFormUtils(currentAddressForm, {
    enableAutocomplete: props.enableAutocomplete,
  })

  const { registerAutoFillAsHankaku } = getHankakuFormUtils(currentAddressForm, {
    enableAutocomplete: props.enableAutocomplete,
  })

  return (
    <ModalEditableForm
      resource={currentAddressPostCode}
      resourceName={resourceName}
      opeHref={null}
      onSubmit={async (e) => {
        e.preventDefault()
        const result = await currentAddressForm.trigger()

        if (!result) {
          showAlert("エラーが発生しました！: 入力内容を確認してください")
        }

        if (result) {
          currentAddressForm.handleSubmit(onSubmit)(e)
        }
      }}
      items={
        currentAddress
          ? [
              ["head", currentAddressPostCode.head],
              ["tail", currentAddressPostCode.tail],
              ["prefecture", currentAddress.prefecture],
              ["prefectureKana", currentAddress.prefectureKana],
              ["line1", currentAddress.line1],
              ["line1Kana", currentAddress.line1Kana],
              ["line2", currentAddress.line2],
              ["line2Kana", currentAddress.line2Kana],
              ["line3", currentAddress.line3],
              ["line3Kana", currentAddress.line3Kana],
              ["buildingName", currentAddress.buildingName],
              ["buildingNameKana", currentAddress.buildingNameKana],
              ["roomNumber", currentAddress.roomNumber],
              ["roomNumberKana", currentAddress.roomNumberKana],
              ["createdAt", formatDate(currentAddressPostCode.createdAt)],
              ["updatedAt", formatDate(currentAddressPostCode.updatedAt)],
            ]
          : [
              ["head", currentAddressPostCode.head],
              ["tail", currentAddressPostCode.tail],
            ]
      }
    >
      <InputField
        className={styles.field}
        label="郵便番号"
        {...currentPostCodeForm.register("zipCode")}
        disabled={currentAddressSWR.isValidating}
      />
      <div className={styles.field} style={{ position: "relative" }}>
        <Text size={12}>
          {addressSWR.isValidating ? "loading..." : null}
          {zipCodeValue && addressSWR.couldNotFindResults ? "住所見つけられなかった…" : null}
        </Text>
      </div>
      <InputField
        className={styles.field}
        label="都道府県"
        {...registerAutoFillAsZenkaku("prefecture")}
        disabled={addressSWR.isValidating}
        validationMessage={currentAddressForm.formState.errors.prefecture?.message}
      />
      <InputField
        className={styles.field}
        label="都道府県カナ"
        {...registerAutoFillAsHankaku("prefectureKana")}
        disabled={addressSWR.isValidating}
        validationMessage={currentAddressForm.formState.errors.prefectureKana?.message}
      />
      <InputField
        className={styles.field}
        label="市区町村"
        {...registerAutoFillAsZenkaku("line1")}
        disabled={addressSWR.isValidating}
        validationMessage={currentAddressForm.formState.errors.line1?.message}
      />
      <InputField
        className={styles.field}
        label="市区町村カナ"
        {...registerAutoFillAsHankaku("line1Kana")}
        disabled={addressSWR.isValidating}
        validationMessage={currentAddressForm.formState.errors.line1Kana?.message}
      />
      <InputField
        className={styles.field}
        label="町名など"
        {...registerAutoFillAsZenkaku("line2")}
        disabled={addressSWR.isValidating}
        validationMessage={currentAddressForm.formState.errors.line2?.message}
      />
      <InputField
        className={styles.field}
        label="町名など（カナ）"
        {...registerAutoFillAsHankaku("line2Kana")}
        disabled={addressSWR.isValidating}
        validationMessage={currentAddressForm.formState.errors.line2Kana?.message}
      />
      <InputField
        label="町名以降"
        className={styles.field}
        {...registerAutoFillAsZenkaku("line3")}
        placeholder="5-20-6"
        validationMessage={currentAddressForm.formState.errors.line3?.message}
      />
      <InputField
        className={styles.field}
        label="町名以降（カナ）"
        {...registerAutoFillAsHankaku("line3Kana")}
        placeholder="5-20-6"
        validationMessage={currentAddressForm.formState.errors.line3Kana?.message}
      />
      <InputField
        className={styles.field}
        label="建物名"
        {...registerAutoFillAsZenkaku("buildingName")}
        placeholder="宇田川町ビルディング"
        validationMessage={currentAddressForm.formState.errors.buildingName?.message}
      />
      <InputField
        className={styles.field}
        label="建物名（カナ）"
        {...registerAutoFillAsHankaku("buildingNameKana")}
        placeholder="ウダガワチョウビルディング"
        validationMessage={currentAddressForm.formState.errors.buildingNameKana?.message}
      />
      <InputField
        className={styles.field}
        label="部屋番号"
        {...registerAutoFillAsZenkaku("roomNumber")}
        placeholder="３０１"
        validationMessage={currentAddressForm.formState.errors.roomNumber?.message}
      />
      <InputField
        className={styles.field}
        label="部屋番号（カナ）"
        {...registerAutoFillAsHankaku("roomNumberKana")}
        placeholder="301"
        validationMessage={currentAddressForm.formState.errors.roomNumberKana?.message}
      />
      <SubmitButton style={{ width: 200, margin: "0 auto" }}>保存</SubmitButton>
    </ModalEditableForm>
  )
}

export const CurrentAddresses: React.FC<{ proposalId: number }> = ({ proposalId }) => {
  const { getWithToken } = useSmoothApi()

  const { data } = useSWR(
    `${SMOOTH_SMOOTHER_API_URL}/proposals/${proposalId}/current_address_post_codes`,
    async (url) => {
      const response = await getWithToken(url)
      return response.data["data"].map((d: CurrentAddressPostCode) =>
        camelCase(d)
      ) as CurrentAddressPostCode[]
    }
  )

  const resourceName = `Proposal.${proposalId}.CurrentAddressPostCode`

  const { setEditingId } = useEditingId(resourceName)

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>
        現住所（{data?.length || 0}）
        <BorderedButton
          onClick={(e) => {
            e.preventDefault()
            setEditingId((c) => {
              if (c === 0) return null
              return 0
            })
          }}
          style={{ marginLeft: "1rem", background: "#ffffff" }}
        >
          新規作成
        </BorderedButton>
      </h2>
      <section>
        {sortById(data || [])?.map((currentAddressPostCode) => {
          return (
            <div key={currentAddressPostCode.id} className={styles.movingApplication}>
              <CurrentAddress
                currentAddressPostCode={currentAddressPostCode}
                proposalId={proposalId}
                resourceName={resourceName}
              />
            </div>
          )
        })}
        {!data?.length ? (
          <div className="not-found">
            現住所が登録されていません
            <CurrentAddress
              currentAddressPostCode={{
                id: 0,
                head: "",
                tail: "",
                createdAt: "",
                updatedAt: "",
                proposalId,
              }}
              proposalId={proposalId}
              resourceName={resourceName}
            />
          </div>
        ) : null}
      </section>
    </div>
  )
}
